import {constructUsing, createMap, forMember, mapFrom, type Mapper} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import {formatTimestamp, type IsNever} from '@igp/shared/utils';

import {
    allColumns,
    type CountryLevelPartnerReportColumnSettingsKeys,
    type CountryLevelPartnerReportDownloadFields,
    type CountryLevelPartnerReportDownloadModel,
    type CountryLevelPartnerReportQueryKeys,
    type CountryLevelPartnerReportSortKeys,
    type CountryLevelPartnerReportViewModel,
    type CountryLevelPartnerReportViewModelKeys,
    type CountryLevelReport,
    summaryId,
} from './types';

export const countryLevelPartnerReportMappingKeys = {
    model: 'CountryLevelPartnerReportModel',
    viewModel: 'CountryLevelPartnerReportViewModel',
    downloadModel: 'CountryLevelPartnerReportDownloadModel',
    viewModelsKeys: 'CountryLevelPartnerReportViewModelKeys',
    queryItemsKeys: 'CountryLevelPartnerReportQueryItemsKeys',
    querySummaryKeys: 'CountryLevelPartnerReportQuerySummaryKeys',
    downloadFields: 'CountryLevelPartnerReportDownloadFields',
    settingKeys: 'CountryLevelPartnerReportColumnSettingKeys',
    sortKeys: 'CountryLevelPartnerReportSortKeys',
} as const;

export class CountryLevelPartnerReportMapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<CountryLevelReport, CountryLevelPartnerReportViewModel>(
            mapper,
            countryLevelPartnerReportMappingKeys.model,
            countryLevelPartnerReportMappingKeys.viewModel,
            forMember(
                vm => vm.casino_ggr,
                mapFrom(m => m.casino_ggr)
            ),
            forMember(
                vm => vm.country,
                mapFrom(m => m.country)
            ),
            forMember(
                vm => vm.daily_active_users,
                mapFrom(m => m.daily_active_users)
            ),
            forMember(
                vm => vm.date_ts,
                mapFrom(m => (!m.is_summary ? m.date_ts : null))
            ),
            forMember(
                vm => vm.deposit_count,
                mapFrom(m => m.deposit_count)
            ),
            forMember(
                vm => vm.deposit_users,
                mapFrom(m => m.deposit_users)
            ),
            forMember(
                vm => vm.first_deposit_amount,
                mapFrom(m => m.first_deposit_amount)
            ),
            forMember(
                vm => vm.first_time_deposit_users,
                mapFrom(m => m.first_time_deposit_users)
            ),
            forMember(
                vm => vm.global_spins_ggr,
                mapFrom(m => m.global_spins_ggr)
            ),
            forMember(
                vm => vm.net_deposit,
                mapFrom(m => m.net_deposit)
            ),
            forMember(
                vm => vm.new_users,
                mapFrom(m => m.new_users)
            ),
            forMember(
                vm => vm.payout_amount,
                mapFrom(m => m.payout_amount)
            ),
            forMember(
                vm => vm.poker_cash_ggr,
                mapFrom(m => m.poker_cash_ggr)
            ),
            forMember(
                vm => vm.poker_mtt_ggr,
                mapFrom(m => m.poker_mtt_ggr)
            ),
            forMember(
                vm => vm.promotional_spend,
                mapFrom(m => m.promotional_spend)
            ),
            forMember(
                vm => vm.retained_deposit_amount,
                mapFrom(m => m.retained_deposit_amount)
            ),
            forMember(
                vm => vm.retained_deposit_users,
                mapFrom(m => m.retained_deposit_users)
            ),
            forMember(
                vm => vm.sports_ggr,
                mapFrom(m => m.sports_ggr)
            ),
            forMember(
                vm => vm.total_bet_amount,
                mapFrom(m => m.total_bet_amount)
            ),
            forMember(
                vm => vm.total_deposit_amount,
                mapFrom(m => m.total_deposit_amount)
            ),
            forMember(
                vm => vm.total_ggr,
                mapFrom(m => m.total_ggr)
            ),
            forMember(
                vm => vm.total_hands,
                mapFrom(m => m.total_hands)
            ),
            forMember(
                vm => vm.total_mtt_entries,
                mapFrom(m => m.total_mtt_entries)
            ),
            forMember(
                vm => vm.total_withdrawal_amount,
                mapFrom(m => m.total_withdrawal_amount)
            ),
            forMember(
                vm => vm.withdrawal_count,
                mapFrom(m => m.withdrawal_count)
            ),
            forMember(
                vm => vm.withdrawal_users,
                mapFrom(m => m.withdrawal_users)
            )
        );
        createMap<CountryLevelReport, CountryLevelPartnerReportDownloadModel>(
            mapper,
            countryLevelPartnerReportMappingKeys.model,
            countryLevelPartnerReportMappingKeys.downloadModel,
            forMember(
                vm => vm.casino_ggr,
                mapFrom(m => m.casino_ggr?.toString())
            ),
            forMember(
                vm => vm.country,
                mapFrom(m => m.country)
            ),
            forMember(
                vm => vm.daily_active_users,
                mapFrom(m => m.daily_active_users?.toString())
            ),
            forMember(
                vm => vm.date_ts,
                mapFrom(m => (!m.is_summary ? formatTimestamp(m.date_ts, 'date')?.trim() : summaryId))
            ),
            forMember(
                vm => vm.deposit_count,
                mapFrom(m => m.deposit_count?.toString())
            ),
            forMember(
                vm => vm.deposit_users,
                mapFrom(m => m.deposit_users?.toString())
            ),
            forMember(
                vm => vm.first_deposit_amount,
                mapFrom(m => m.first_deposit_amount?.toString())
            ),
            forMember(
                vm => vm.first_time_deposit_users,
                mapFrom(m => m.first_time_deposit_users?.toString())
            ),
            forMember(
                vm => vm.global_spins_ggr,
                mapFrom(m => m.global_spins_ggr?.toString())
            ),
            forMember(
                vm => vm.net_deposit,
                mapFrom(m => m.net_deposit?.toString())
            ),
            forMember(
                vm => vm.new_users,
                mapFrom(m => m.new_users?.toString())
            ),
            forMember(
                vm => vm.payout_amount,
                mapFrom(m => m.payout_amount?.toString())
            ),
            forMember(
                vm => vm.poker_cash_ggr,
                mapFrom(m => m.poker_cash_ggr?.toString())
            ),
            forMember(
                vm => vm.poker_mtt_ggr,
                mapFrom(m => m.poker_mtt_ggr?.toString())
            ),
            forMember(
                vm => vm.promotional_spend,
                mapFrom(m => m.promotional_spend?.toString())
            ),
            forMember(
                vm => vm.retained_deposit_amount,
                mapFrom(m => m.retained_deposit_amount?.toString())
            ),
            forMember(
                vm => vm.retained_deposit_users,
                mapFrom(m => m.retained_deposit_users?.toString())
            ),
            forMember(
                vm => vm.sports_ggr,
                mapFrom(m => m.sports_ggr?.toString())
            ),
            forMember(
                vm => vm.total_bet_amount,
                mapFrom(m => m.total_bet_amount?.toString())
            ),
            forMember(
                vm => vm.total_deposit_amount,
                mapFrom(m => m.total_deposit_amount?.toString())
            ),
            forMember(
                vm => vm.total_ggr,
                mapFrom(m => m.total_ggr?.toString())
            ),
            forMember(
                vm => vm.total_hands,
                mapFrom(m => m.total_hands?.toString())
            ),
            forMember(
                vm => vm.total_mtt_entries,
                mapFrom(m => m.total_mtt_entries?.toString())
            ),
            forMember(
                vm => vm.total_withdrawal_amount,
                mapFrom(m => m.total_withdrawal_amount?.toString())
            ),
            forMember(
                vm => vm.withdrawal_count,
                mapFrom(m => m.withdrawal_count?.toString())
            ),
            forMember(
                vm => vm.withdrawal_users,
                mapFrom(m => m.withdrawal_users?.toString())
            )
        );
        createMap<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            countryLevelPartnerReportMappingKeys.queryItemsKeys,
            constructUsing(m => {
                const mapper: Record<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys> = {
                    casino_ggr: 'items.casino_ggr',
                    country: 'items.country',
                    daily_active_users: 'items.daily_active_users',
                    date_ts: 'items.date_ts.seconds',
                    deposit_count: 'items.deposit_count',
                    deposit_users: 'items.deposit_users',
                    first_deposit_amount: 'items.first_deposit_amount',
                    first_time_deposit_users: 'items.first_time_deposit_users',
                    global_spins_ggr: 'items.global_spins_ggr',
                    net_deposit: 'items.net_deposit',
                    new_users: 'items.new_users',
                    payout_amount: 'items.payout_amount',
                    poker_cash_ggr: 'items.poker_cash_ggr',
                    poker_mtt_ggr: 'items.poker_mtt_ggr',
                    promotional_spend: 'items.promotional_spend',
                    retained_deposit_amount: 'items.retained_deposit_amount',
                    retained_deposit_users: 'items.retained_deposit_users',
                    sports_ggr: 'items.sports_ggr',
                    total_bet_amount: 'items.total_bet_amount',
                    total_deposit_amount: 'items.total_deposit_amount',
                    total_ggr: 'items.total_ggr',
                    total_hands: 'items.total_hands',
                    total_mtt_entries: 'items.total_mtt_entries',
                    total_withdrawal_amount: 'items.total_withdrawal_amount',
                    withdrawal_count: 'items.withdrawal_count',
                    withdrawal_users: 'items.withdrawal_users',
                };
                return mapper[m];
            })
        );
        createMap<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            countryLevelPartnerReportMappingKeys.querySummaryKeys,
            constructUsing(m => {
                const mapper: Record<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys> = {
                    casino_ggr: 'summary.casino_ggr',
                    country: null,
                    daily_active_users: 'summary.daily_active_users',
                    date_ts: null,
                    deposit_count: 'summary.deposit_count',
                    deposit_users: 'summary.deposit_users',
                    first_deposit_amount: 'summary.first_deposit_amount',
                    first_time_deposit_users: 'summary.first_time_deposit_users',
                    global_spins_ggr: 'summary.global_spins_ggr',
                    net_deposit: 'summary.net_deposit',
                    new_users: 'summary.new_users',
                    payout_amount: 'summary.payout_amount',
                    poker_cash_ggr: 'summary.poker_cash_ggr',
                    poker_mtt_ggr: 'summary.poker_mtt_ggr',
                    promotional_spend: 'summary.promotional_spend',
                    retained_deposit_amount: 'summary.retained_deposit_amount',
                    retained_deposit_users: 'summary.retained_deposit_users',
                    sports_ggr: 'summary.sports_ggr',
                    total_bet_amount: 'summary.total_bet_amount',
                    total_deposit_amount: 'summary.total_deposit_amount',
                    total_ggr: 'summary.total_ggr',
                    total_hands: 'summary.total_hands',
                    total_mtt_entries: 'summary.total_mtt_entries',
                    total_withdrawal_amount: 'summary.total_withdrawal_amount',
                    withdrawal_count: 'summary.withdrawal_count',
                    withdrawal_users: 'summary.withdrawal_users',
                };
                return mapper[m];
            })
        );
        createMap<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportDownloadFields>(
            mapper,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            countryLevelPartnerReportMappingKeys.downloadFields,
            constructUsing(m => {
                const mapper: Record<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportDownloadFields> = {
                    casino_ggr: 'casino_ggr',
                    country: 'country',
                    daily_active_users: 'daily_active_users',
                    date_ts: 'date_ts.seconds',
                    deposit_count: 'deposit_count',
                    deposit_users: 'deposit_users',
                    first_deposit_amount: 'first_deposit_amount',
                    first_time_deposit_users: 'first_time_deposit_users',
                    global_spins_ggr: 'global_spins_ggr',
                    net_deposit: 'net_deposit',
                    new_users: 'new_users',
                    payout_amount: 'payout_amount',
                    poker_cash_ggr: 'poker_cash_ggr',
                    poker_mtt_ggr: 'poker_mtt_ggr',
                    promotional_spend: 'promotional_spend',
                    retained_deposit_amount: 'retained_deposit_amount',
                    retained_deposit_users: 'retained_deposit_users',
                    sports_ggr: 'sports_ggr',
                    total_bet_amount: 'total_bet_amount',
                    total_deposit_amount: 'total_deposit_amount',
                    total_ggr: 'total_ggr',
                    total_hands: 'total_hands',
                    total_mtt_entries: 'total_mtt_entries',
                    total_withdrawal_amount: 'total_withdrawal_amount',
                    withdrawal_count: 'withdrawal_count',
                    withdrawal_users: 'withdrawal_users',
                };
                return mapper[m];
            })
        );
        createMap<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportColumnSettingsKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            countryLevelPartnerReportMappingKeys.settingKeys,
            constructUsing(m => {
                type FieldsToMap = Exclude<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportColumnSettingsKeys>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return allColumns.includes(m) ? m : undefined;
            })
        );
        createMap<CountryLevelPartnerReportColumnSettingsKeys, CountryLevelPartnerReportViewModelKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.settingKeys,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            constructUsing(m => {
                type FieldsToMap = Exclude<CountryLevelPartnerReportColumnSettingsKeys, CountryLevelPartnerReportViewModelKeys | '__type'>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return allColumns.includes(m) ? m : undefined;
            })
        );
        createMap<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportSortKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            countryLevelPartnerReportMappingKeys.sortKeys,
            constructUsing(m => {
                const mapper: Record<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportSortKeys> = {
                    casino_ggr: 'casino_ggr',
                    country: 'country',
                    daily_active_users: 'daily_active_users',
                    date_ts: 'date_ts',
                    deposit_count: 'deposit_count',
                    deposit_users: 'deposit_users',
                    first_deposit_amount: 'first_deposit_amount',
                    first_time_deposit_users: 'first_time_deposit_users',
                    global_spins_ggr: 'global_spins_ggr',
                    net_deposit: 'net_deposit',
                    new_users: 'new_users',
                    payout_amount: 'payout_amount',
                    poker_cash_ggr: 'poker_cash_ggr',
                    poker_mtt_ggr: 'poker_mtt_ggr',
                    promotional_spend: 'promotional_spend',
                    retained_deposit_amount: 'retained_deposit_amount',
                    retained_deposit_users: 'retained_deposit_users',
                    sports_ggr: 'sports_ggr',
                    total_bet_amount: 'total_bet_amount',
                    total_deposit_amount: 'total_deposit_amount',
                    total_ggr: 'total_ggr',
                    total_hands: 'total_hands',
                    total_mtt_entries: 'total_mtt_entries',
                    total_withdrawal_amount: 'total_withdrawal_amount',
                    withdrawal_count: 'withdrawal_count',
                    withdrawal_users: 'withdrawal_users',
                };
                return mapper[m];
            })
        );
        createMap<CountryLevelPartnerReportSortKeys, CountryLevelPartnerReportViewModelKeys>(
            mapper,
            countryLevelPartnerReportMappingKeys.sortKeys,
            countryLevelPartnerReportMappingKeys.viewModelsKeys,
            constructUsing(m => {
                const mapper: Record<CountryLevelPartnerReportSortKeys, CountryLevelPartnerReportViewModelKeys> = {
                    casino_ggr: 'casino_ggr',
                    country: 'country',
                    daily_active_users: 'daily_active_users',
                    date_ts: 'date_ts',
                    deposit_count: 'deposit_count',
                    deposit_users: 'deposit_users',
                    first_deposit_amount: 'first_deposit_amount',
                    first_time_deposit_users: 'first_time_deposit_users',
                    global_spins_ggr: 'global_spins_ggr',
                    net_deposit: 'net_deposit',
                    new_users: 'new_users',
                    payout_amount: 'payout_amount',
                    poker_cash_ggr: 'poker_cash_ggr',
                    poker_mtt_ggr: 'poker_mtt_ggr',
                    promotional_spend: 'promotional_spend',
                    retained_deposit_amount: 'retained_deposit_amount',
                    retained_deposit_users: 'retained_deposit_users',
                    sports_ggr: 'sports_ggr',
                    total_bet_amount: 'total_bet_amount',
                    total_deposit_amount: 'total_deposit_amount',
                    total_ggr: 'total_ggr',
                    total_hands: 'total_hands',
                    total_mtt_entries: 'total_mtt_entries',
                    total_withdrawal_amount: 'total_withdrawal_amount',
                    withdrawal_count: 'withdrawal_count',
                    withdrawal_users: 'withdrawal_users',
                };
                return mapper[m];
            })
        );
    }
}
